import 'maplibre-gl/dist/maplibre-gl.css';

import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Link } from '@abyss/web/ui/Link';
import maplibregl from 'maplibre-gl';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { ChipFilters } from '../../common/ChipFiltersGroup';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { ResultFilter } from '../../common/ContentFilters/ResultFilter';
import { ResultSort } from '../../common/ContentFilters/ResultSort';
import { PortalContext } from '../../context/PortalContext';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { useConfig } from '../../frontends/ProviderSearch/context/Analytics';
import { getEnableMapPinRedesign } from '../../frontends/ProviderSearch/routes/MapView/utils';
import { useCoverageType } from '../../hooks/useCoverageType';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { FacilityLocationsResponse } from '../../models/FacilityDetails';
import { Provider } from '../../models/Provider';
import { ProviderLocation } from '../../models/ProviderDetails';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { Directions } from '../../models/RouteDirections';
import { useMapStore } from '../../store/useMapStore';
import { MapState } from '../../store/useMapStore/mapStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import {
  Feature,
  clearRoute,
  getAmazonRoute,
  getFeaturesArray,
  getUniqueCoordinates,
  loadAmazonRoute,
  loadClusteredLocationPins,
  loadMap,
  loadResultLocationPins,
  loadUserLocationPin,
  removeAllMarkers,
  setZoomLevel,
} from '../../utils/amazon.map.utils';
import {
  getDistanceString,
  getDurationString,
  getNavTypeString,
  getStepDistanceString,
  getStepIcon,
} from '../../utils/generalMap.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { Constants, ReverseCoverageTypesCodes } from '../Constants';
import { mobileOnly } from '../ConstantsStyles';
import { PortalIdentifier } from '../Portal';
import { ClusterCarousel } from './AmazonClusterCarousel';
import { MapControlDesktop } from './AmazonMapControlDesktop';
import { MapControlMobile } from './AmazonMapControlMobile';
import { MapDirections } from './AmazonMapDirections';
import {
  MapContainer,
  MapDirectionsContainer,
  NavigationContainer,
  NavigationContainer2,
} from './AmazonMapDisplay.styled';

type Props = {
  closeSidePanel?: boolean;
  navigationRef?: React.MutableRefObject<HTMLElement | null>;
  coords: { latitude: string; longitude: string };
  disabledPinAction?: boolean;
  fullWidth?: boolean;
  headers?: ResponseHeaders;
  locationResults?: ProviderLocation[] | FacilityLocationsResponse[];
  map: any;
  isOpenCompareShare?: boolean;
  providerPins?: Provider[];
  providerResults?: Provider[];
  routeEndCoords?: [number | null, number | null];
  setRouteEndCoords?(coords: [number | null, number | null]): void;
  selectedItems?: any[];
  mobileMapControlChildren?: React.ReactNode;
  mobileRouteView?: boolean;
  setPopupContent?(
    content: ProviderLocation | FacilityLocationsResponse | null
  ): void;
  refToBeFocusedOnDirectionsClose?: HTMLElement;
  updatePin?(
    content: ProviderLocation | FacilityLocationsResponse | null
  ): void;
  selectLocation?: (locationId, locationLng, locationLat) => void;
  directions?: Directions;
  mobileScreen?: boolean;
  isLocationsTab?: boolean;
  previousLocation?: string;
  setPreviousLocation?: (a: string) => void;
  enableMapFilterSortCTA?: boolean;
  expandedProviderListView?: boolean;
  setExpandedProviderListView?: (a: boolean) => void;
  setNavigateToDirections?: (a: boolean) => void;
  setGroupedLocationDrawerOpen?: (a: boolean) => void;
  setDrawerContent?: (a: any) => void;
  skipNavigationFocus?: boolean;
  onClick?: () => void;
  shouldResetSelectedId?: boolean;
};

export const AmazonMapDisplay = ({
  coords,
  disabledPinAction = false,
  fullWidth = false,
  isOpenCompareShare = false,
  providerPins = [],
  providerResults = [],
  locationResults = [],
  map,
  closeSidePanel,
  routeEndCoords = [null, null],
  setRouteEndCoords,
  selectedItems,
  mobileMapControlChildren,
  mobileRouteView,
  setPopupContent,
  refToBeFocusedOnDirectionsClose,
  updatePin,
  selectLocation,
  directions,
  headers,
  mobileScreen = false,
  isLocationsTab = false,
  previousLocation,
  setPreviousLocation,
  enableMapFilterSortCTA,
  expandedProviderListView,
  setExpandedProviderListView,
  setNavigateToDirections,
  setGroupedLocationDrawerOpen,
  setDrawerContent,
  skipNavigationFocus = false,
}: Props) => {
  const { t } = useTranslation();
  const mapContainerTestId: string = 'map-view-container';
  const API_KEY = useConfig('AWS_MAP_KEY');
  const REGION = 'us-east-1';
  // AWS Location Service supported styles: Standard, Monochrome, Hybrid, Satellite
  // Standard = Street map view, Hybrid = Satellite + labels, Satellite = Satellite only
  const STYLE = 'Standard';
  const COLOR_SCHEME = 'Light';
  const { longitude, latitude } = coords;
  const enableScreenZoom = useMediaQuery(mobileOnly);

  const clusterMaxZoom = useConfig('MAP_CLUSTER_MAX_ZOOM');
  const clusterRadius = useConfig('MAP_CLUSTER_RADIUS');
  const groupDistanceThreshold = useConfig(
    'MAP_GROUP_DISTANCE_THRESHOLD_MILES'
  );
  const { portalData } = useContext(PortalContext);

  const [navType, setNavType] = useState<string>('driving-traffic');
  const [clusterStatus, setClusterStatus] = useState<boolean>(false);
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const [currentRoute, setCurrentRoute] = useState<number>(0);
  const [showStepInstructions, setShowStepInstructions] =
    useState<boolean>(false);
  const [sortFilterOpen, setSortFilterOpen] = useState(
    storage.session.get('sortFilterOpen')
  );
  const [allFiltersOpen, setAllFiltersOpen] = useState(
    storage.session.get('allFiltersOpen')
  );

  const [mapDirections, setMapDirections] = useState<Directions>(
    directions || {
      userPlaceName: '',
      endPlaceName: '',
      routes: [],
    }
  );

  const mapContainerRef = React.useRef<any>(null);
  const navigationContainerRef = useRef<HTMLElement>();

  const [highlightId, setHighlightId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_HIGHLIGHT_ID,
    { providerId: '', from: '' }
  );

  const [, setSelectedId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    null
  );

  const [, setMapPinCoords] = useSessionStorage<
    (string[] | (string | undefined)[])[]
  >(Constants.STORAGE_KEYS.SESSION.MAP_PIN_COORDS, []);

  const [
    enableMapEnhancements,
    enableMapViewLargeResults,
    enableMapPinRedesignFlag,
    enableMapDragRefreshResults,
    enableMapViewHideOption,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_LARGE_RESULTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_PIN_REDESIGN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_DRAG_REFRESH_RESULTS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_HIDE_OPTION,
  ]);

  const forceMapPinRedesign = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN
  );
  const enableMapPinRedesign = getEnableMapPinRedesign(
    enableMapPinRedesignFlag,
    forceMapPinRedesign
  );

  const { setMapLatitude, setMapLongitude, setMapSearchRadius } = useMapStore(
    useShallow((state: MapState) => ({
      setMapLatitude: state.setMapLatitude,
      setMapLongitude: state.setMapLongitude,
      setMapSearchRadius: state.setMapSearchRadius,
    }))
  );

  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;

  const { search, sectionType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
      sectionType: state.typeaheadSearchStore.sectionType,
    }))
  );

  const handleMapPins = async (
    featuresAr: Feature[],
    longitude: string,
    latitude: string,
    mobileScreen: boolean,
    map: maplibregl.Map,
    setHighlightId: (highlightData: {
      providerId: string;
      from: string;
    }) => void,
    disabledPinAction: boolean,
    updatePinFn: Props['updatePin'],
    enableMapEnhancements: boolean,
    enableMapViewLargeResults: boolean,
    setMapPinCoordsFn: (coords: (string[] | (string | undefined)[])[]) => void,
    removeAllMarkersFn: () => void,
    setZoomLevelFn: typeof setZoomLevel,
    loadResultLocationPinsFn: typeof loadResultLocationPins,
    enableMapPinRedesign: boolean
  ): Promise<void> => {
    if (!featuresAr?.length) {
      return;
    }
    const uniqueCoordinates = getUniqueCoordinates(
      featuresAr,
      longitude,
      latitude
    );
    removeAllMarkersFn();
    if (enableMapPinRedesign) {
      await loadClusteredLocationPins(
        map,
        featuresAr,
        setHighlightId,
        updatePinFn,
        enableMapEnhancements,
        mobileScreen,
        disabledPinAction,
        enableMapViewLargeResults,
        clusterMaxZoom,
        clusterRadius,
        groupDistanceThreshold,
        setGroupedLocationDrawerOpen,
        setDrawerContent
      );
    } else {
      setMapPinCoordsFn(uniqueCoordinates);
      await loadResultLocationPinsFn(
        mobileScreen,
        map,
        featuresAr,
        uniqueCoordinates,
        setHighlightId,
        disabledPinAction,
        updatePinFn,
        enableMapEnhancements,
        enableMapViewLargeResults
      );
    }
    await setZoomLevelFn(map, uniqueCoordinates);
  };

  useEffect(() => {
    if (!map.current) {
      map.current = new maplibregl.Map({
        container: mapContainerRef.current,
        style: `https://maps.geo.${REGION}.amazonaws.com/v2/styles/${STYLE}/descriptor?key=${API_KEY}&color-scheme=${COLOR_SCHEME}`,
        center: [Number(longitude), Number(latitude)],
        zoom: Constants.DEVICE_LOCATION.ZOOM,
        // Improve text rendering for better street name visibility
        localIdeographFontFamily: "'Noto Sans', 'Arial Unicode MS', sans-serif",
        pitchWithRotate: false,
        dragRotate: false, // Disable rotation for clearer text reading
      });
    }
    clearRoute(map.current, setRouteEndCoords);
    setHighlightId({ providerId: '', from: '' });

    map.current.on('load', async () => {
      loadMap(
        map.current,
        setHighlightId,
        setPopupContent,
        enableMapEnhancements
      );
      loadUserLocationPin(map.current, longitude, latitude, isGeha);

      const featuresAr = getFeaturesArray(
        enableMapPinRedesign,
        isOpenCompareShare,
        providerPins,
        providerResults,
        locationResults
      );

      handleMapPins(
        featuresAr,
        longitude,
        latitude,
        mobileScreen,
        map.current,
        setHighlightId,
        disabledPinAction,
        updatePin,
        enableMapEnhancements,
        enableMapViewLargeResults,
        setMapPinCoords,
        removeAllMarkers,
        setZoomLevel,
        loadResultLocationPins,
        enableMapPinRedesign
      );
      setIsMapLoaded(true);

      // Enable POI layers after pins are loaded (non-blocking)
      // setTimeout(() => {
      //   try {
      //     const poiLayers = [
      //       'poi_900_areas_buildings',
      //       'poi_700_business_services',
      //       'poi_600_shopping',
      //       'poi_550_leisure_outdoor',
      //       'poi_800_facilities',
      //       'poi_400_transit',
      //       'poi_300_sights_museums',
      //       'poi_100_food_drink',
      //       'poi_200_going_out_entertainment',
      //       'poi_500_accommodations',
      //       'poi_landuse_park',
      //       'poi_landuse_public_complex',
      //     ];

      //     poiLayers.forEach((layerId) => {
      //       if (map.current?.getLayer(layerId)) {
      //         map.current.setLayoutProperty(layerId, 'visibility', 'visible');
      //       }
      //     });

      //     // Force street name labels to show at all zoom levels
      //     const style = map.current?.getStyle();
      //     if (style?.layers) {
      //       style.layers.forEach((layer: any) => {
      //         // Target road/street label layers
      //         if (
      //           layer.id?.includes('road') ||
      //           layer.id?.includes('street') ||
      //           layer.id?.includes('label') ||
      //           layer.id?.includes('place')
      //         ) {
      //           if (layer.type === 'symbol' && map.current.getLayer(layer.id)) {
      //             // Make text labels always visible
      //             try {
      //               map.current.setLayoutProperty(
      //                 layer.id,
      //                 'visibility',
      //                 'visible'
      //               );
      //               // Force text to show at low zoom levels with larger size
      //               map.current.setLayoutProperty(layer.id, 'text-size', [
      //                 'interpolate',
      //                 ['linear'],
      //                 ['zoom'],
      //                 0,
      //                 14, // Start at zoom 0 with size 14
      //                 10,
      //                 14, // Size 14 at zoom 10
      //                 15,
      //                 16, // Size 16 at zoom 15
      //                 22,
      //                 18, // Size 18 at zoom 22
      //               ]);
      //               // Make text always show even when it collides
      //               map.current.setLayoutProperty(
      //                 layer.id,
      //                 'text-allow-overlap',
      //                 true
      //               );
      //               map.current.setLayoutProperty(
      //                 layer.id,
      //                 'text-ignore-placement',
      //                 true
      //               );
      //               // Increase text opacity
      //               map.current.setPaintProperty(layer.id, 'text-opacity', 1);
      //             } catch {
      //               // Silently continue if property can't be set
      //             }
      //           }
      //         }
      //       });
      //     }
      //   } catch {
      //     // Failed to enable POI/street layers
      //   }
      // }, 100);

      map.current.on('moveend', (e) => {
        if (
          enableMapPinRedesign &&
          enableMapDragRefreshResults &&
          !isOpenCompareShare &&
          'originalEvent' in e
        ) {
          // TODO: integrate debounced setMapCoords for drag-refresh
        }
      });
    });

    return () => {
      if (map.current) {
        map.current.remove(); // Clean up the map instance
        map.current = null;
      }
    };
    // Add dependencies as needed
  }, [longitude, latitude]);

  useEffect(() => {
    setMapLatitude(undefined);
    setMapLongitude(undefined);
    setMapSearchRadius(undefined);
    removeAllMarkers();
    setMapPinCoords([]);

    return () => {
      setMapLatitude(undefined);
      setMapLongitude(undefined);
      setMapSearchRadius(undefined);
      removeAllMarkers();
      setMapPinCoords([]);
    };
  }, []);

  useEffect(() => {
    if (sortFilterOpen || allFiltersOpen) {
      handleCloseNav();
    }

    const intervalId = setInterval(() => {
      const currentSortFilterOpen = storage.session.get('sortFilterOpen');
      const currentAllFiltersOpen = storage.session.get('allFiltersOpen');
      if (currentSortFilterOpen !== sortFilterOpen) {
        setSortFilterOpen(currentSortFilterOpen);
      }
      if (currentAllFiltersOpen !== allFiltersOpen) {
        setAllFiltersOpen(currentAllFiltersOpen);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [sortFilterOpen, allFiltersOpen]);

  useEffect(() => {
    if (directions) {
      setMapDirections(directions);
      setShowStepInstructions(false);
      setCurrentRoute(0);
    }
  }, [directions]);

  useEffect(() => {
    if (!highlightId.from) {
      setClusterStatus(false);
    }
  }, [highlightId]);

  useEffect(() => {
    setMapLatitude(undefined);
    setMapLongitude(undefined);
    setMapSearchRadius(undefined);
    removeAllMarkers();
    setMapPinCoords([]);
    if (isMapLoaded) {
      loadUserLocationPin(map.current, longitude, latitude, isGeha);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (!isMapLoaded) {
      const featuresAr = getFeaturesArray(
        enableMapPinRedesign,
        isOpenCompareShare,
        providerPins,
        providerResults,
        locationResults
      );

      handleMapPins(
        featuresAr,
        longitude,
        latitude,
        mobileScreen,
        map.current,
        setHighlightId,
        disabledPinAction,
        updatePin,
        enableMapEnhancements,
        enableMapViewLargeResults,
        setMapPinCoords,
        removeAllMarkers,
        setZoomLevel,
        loadResultLocationPins,
        enableMapPinRedesign
      );
    }
  }, [
    enableMapPinRedesign
      ? JSON.stringify(providerPins)
      : JSON.stringify(providerResults),
    JSON.stringify(locationResults),
    selectedItems,
    isOpenCompareShare,
  ]);

  useEffect(() => {
    const getMapRoute = async () => {
      const routeDirections = await getAmazonRoute(
        map.current,
        longitude,
        latitude,
        routeEndCoords[0],
        routeEndCoords[1],
        navType,
        0,
        API_KEY
      );
      if (routeDirections) setMapDirections(routeDirections);
    };
    if (routeEndCoords[0]) {
      setCurrentRoute(0);
      getMapRoute();
    }
  }, [navType, map.current, routeEndCoords[0], routeEndCoords[1]]);

  const hasRoute = routeEndCoords[0];

  useEffect(() => {
    setNavType('driving-traffic');
    if (!isWidget && !skipNavigationFocus)
      if (hasRoute === null) {
        refToBeFocusedOnDirectionsClose?.focus();
      } else {
        navigationContainerRef?.current?.focus();
      }
  }, [hasRoute]);

  useEffect(() => {
    if (map.current) map.current.resize();
  }, [closeSidePanel, mobileRouteView]);

  const handleCloseNav = () => {
    clearRoute(map.current, setRouteEndCoords, highlightId.providerId);
    setSelectedId(null);
  };

  const handleBackList = () => {
    adobeLinkTrackEvent({
      name: 'back',
      location: `body:view on list`,
      type: 'internal',
    });

    setNavigateToDirections(false);
    setExpandedProviderListView(true);
  };

  const mapControl = () => {
    const screen = enableMapEnhancements ? enableScreenZoom : mobileScreen;
    if (mobileScreen) {
      return (
        <MapControlMobile
          controlChildren={mobileMapControlChildren}
          enableMapEnhancements={enableMapEnhancements}
          isLocationsTab={isLocationsTab}
          map={map.current}
          mobileRouteView={!!routeEndCoords[0]}
        />
      );
    }

    return (
      <MapControlDesktop
        map={map.current}
        showZoomButtons={enableMapEnhancements && screen}
      />
    );
  };

  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const urlParams = new URLSearchParams(window.location.search);
  const choosePCPID = urlParams.get('choosePCPId') || '';

  const coverageTypeData = useCoverageType();
  const resultFilterName =
    coverageTypeData === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
      ? t('FILTER_BUTTONS.All_FILTERS')
      : t('CONTENT_FILTERS.ALL_FILTERS');

  const mapDirectionsProps = {
    currentRoute,
    getDistanceString,
    getDurationString,
    getNavTypeString,
    getStepDistanceString,
    getStepIcon,
    handleCloseNav,
    latitude,
    loadRoute: loadAmazonRoute,
    longitude,
    map,
    mapContainerRef,
    mapContainerTestId,
    mapDirections,
    mobileScreen,
    navType,
    routeEndCoords,
    setCurrentRoute,
    setNavType,
    setShowStepInstructions,
    showStepInstructions,
  };

  const filterSortFlex = (
    <Flex alignItems="flex-end" justify="start" style={{ gap: '16px' }}>
      <ResultFilter
        color="$descriptionBlack"
        icon="filter_list"
        name={resultFilterName}
        size="$md"
      />
      <ResultSort isMapView sectionType={sectionType} />
    </Flex>
  );

  const renderOldMap = () => (
    <NavigationContainer
      cssProps={{
        hasRoute,
        enableMapEnhancements: !enableMapFilterSortCTA,
      }}
      id="navigation-container-id"
      // @ts-expect-error styled-component ref type mismatch
      ref={navigationContainerRef}
      tabIndex={-1}
    >
      {hasRoute && mapDirections?.routes?.length && !isWidget ? (
        <MapDirections {...mapDirectionsProps} />
      ) : (
        <>
          {(enableMapFilterSortCTA ||
            (enableMapEnhancements && !mobileScreen)) &&
            filterSortFlex}
        </>
      )}
    </NavigationContainer>
  );
  const renderNewMap = () => (
    <NavigationContainer2
      cssProps={{ isLocationsTab, mobileScreen, hasRoute }}
      id="navigation-container-id"
      // @ts-expect-error styled-component ref type mismatch
      ref={navigationContainerRef}
      tabIndex={-1}
    >
      {hasRoute && mapDirections?.routes?.length && !isWidget ? (
        <>
          {!enableMapFilterSortCTA &&
            !isLocationsTab &&
            !mobileScreen &&
            choosePCPID.length <= 0 &&
            !isWidget &&
            filterSortFlex}
          <MapDirectionsContainer
            // @ts-expect-error styled-component cssProps type mismatch
            cssProps={{ isLocationsTab, mobileScreen }}
          >
            <MapDirections {...mapDirectionsProps} />
          </MapDirectionsContainer>
        </>
      ) : (
        <>
          {!enableMapFilterSortCTA &&
            enableMapEnhancements &&
            !mobileScreen &&
            !isLocationsTab &&
            !window.location.href?.includes('choose-pcp') &&
            choosePCPID.length <= 0 &&
            !isWidget && (
              <Flex
                alignItems="flex-end"
                css={{
                  '@media (max-width: 964px)': {
                    maxWidth: '375px',
                  },
                  '@media (max-width: 771px)': {
                    maxWidth: '180px',
                  },
                }}
                justify="start"
                style={{ gap: '16px' }}
              >
                <ResultFilter
                  color="$descriptionBlack"
                  icon="filter_list"
                  name={resultFilterName}
                  size="$md"
                />
                <ErrorBoundary>
                  <ChipFilters
                    enableMapFilterSortCTA={enableMapFilterSortCTA}
                    previousLocation={previousLocation}
                    sectionType={sectionType || ''}
                    setPreviousLocation={setPreviousLocation}
                  />
                </ErrorBoundary>
                <ResultSort isMapView sectionType={sectionType} />
              </Flex>
            )}
        </>
      )}
    </NavigationContainer2>
  );

  return (
    <React.Fragment>
      {!mobileScreen && enableMapViewHideOption && expandedProviderListView && (
        <Flex css={{ margin: '24px 0 24px 16px' }}>
          <Link
            data-auto-testid="back-to-provider-list-view"
            data-testid="back-to-provider-list-view"
            isStandardAnchor
            onClick={() => handleBackList()}
          >
            <IconMaterial
              color="$interactive1"
              css={{ width: '20px', height: '20px', marginRight: '4px' }}
              icon="arrow_back"
              title="Back_to-results"
            />
            {t('BACK_TO_RESULTS')}
          </Link>
        </Flex>
      )}
      <MapContainer
        // @ts-expect-error styled-component cssProps type mismatch
        cssProps={{
          enableMapEnhancements,
          fullWidth,
          clusterStatus,
          isOpenCompareShare,
          mobileRouteView,
          hasRoute,
          isWidget,
          isGeha,
        }}
      >
        <div
          className="map-container"
          data-auto-testid={mapContainerTestId}
          data-testid={mapContainerTestId}
          ref={mapContainerRef}
        />
        {mapControl()}
        {enableMapEnhancements ? renderNewMap() : renderOldMap()}
      </MapContainer>
      <ClusterCarousel
        data={[]}
        headers={headers}
        search={search}
        sectionType={sectionType}
        selectLocation={selectLocation}
      />
    </React.Fragment>
  );
};
